
import React, { createContext, useContext, useEffect, ReactNode } from 'react';
import { trpc } from '@/utils/api-client';
import { tokens } from '@/lib/palette';

export interface WhitelabelSettings {
  portalName: string;
  logoUrl: string | null;
  faviconUrl: string | null;
  primaryColor: string;
  secondaryColor: string;
  footerText: string;
  customCss: string;
}

interface WhitelabelContextType {
  settings: WhitelabelSettings;
  isLoading: boolean;
}

const defaultSettings: WhitelabelSettings = {
  portalName: 'Smart City TVMS',
  logoUrl: null,
  faviconUrl: null,
  primaryColor: tokens.primary,
  secondaryColor: tokens.mutedForeground,
  footerText: '',
  customCss: '',
};

const WhitelabelContext = createContext<WhitelabelContextType>({
  settings: defaultSettings,
  isLoading: true,
});

export function useWhitelabel() {
  return useContext(WhitelabelContext);
}

export function WhitelabelProvider({ children }: { children: ReactNode }) {
  const { data, isLoading } = trpc.whitelabel.getSettings.useQuery(undefined, {
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
  });

  const settings: WhitelabelSettings = {
    portalName: data?.portalName || defaultSettings.portalName,
    logoUrl: data?.logoUrl || defaultSettings.logoUrl,
    faviconUrl: data?.faviconUrl || defaultSettings.faviconUrl,
    primaryColor: data?.primaryColor || defaultSettings.primaryColor,
    secondaryColor: data?.secondaryColor || defaultSettings.secondaryColor,
    footerText: data?.footerText || defaultSettings.footerText,
    customCss: data?.customCss || defaultSettings.customCss,
  };

  // Apply CSS variables for brand colors
  useEffect(() => {
    if (isLoading) return;
    const root = document.documentElement;
    // Legacy vars (kept for any tenant customCss that references them)
    root.style.setProperty('--whitelabel-primary', settings.primaryColor);
    root.style.setProperty('--whitelabel-secondary', settings.secondaryColor);
    // Drive the actual design-system primary tokens so tenant branding reskins
    // buttons, links, focus rings and sidebar highlights. Only override when the
    // tenant has set a brand color (data from server) — otherwise keep the
    // default theme tokens defined in globals.css.
    if (data?.primaryColor) {
      for (const v of ['--primary', '--ring', '--sidebar-primary', '--sidebar-ring']) {
        root.style.setProperty(v, data.primaryColor);
      }
    } else {
      for (const v of ['--primary', '--ring', '--sidebar-primary', '--sidebar-ring']) {
        root.style.removeProperty(v);
      }
    }
  }, [isLoading, data?.primaryColor, settings.primaryColor, settings.secondaryColor]);

  // Apply document title
  useEffect(() => {
    if (isLoading) return;
    document.title = settings.portalName;
  }, [isLoading, settings.portalName]);

  // Apply favicon
  useEffect(() => {
    if (isLoading || !settings.faviconUrl) return;
    let link = document.querySelector("link[rel~='icon']") as HTMLLinkElement | null;
    if (!link) {
      link = document.createElement('link');
      link.rel = 'icon';
      document.head.appendChild(link);
    }
    link.href = settings.faviconUrl;
  }, [isLoading, settings.faviconUrl]);

  // Inject custom CSS
  useEffect(() => {
    if (isLoading) return;
    const styleId = 'whitelabel-custom-css';
    let style = document.getElementById(styleId) as HTMLStyleElement | null;

    if (settings.customCss) {
      if (!style) {
        style = document.createElement('style');
        style.id = styleId;
        document.head.appendChild(style);
      }
      style.textContent = settings.customCss;
    } else if (style) {
      style.remove();
    }
  }, [isLoading, settings.customCss]);

  return (
    <WhitelabelContext.Provider value={{ settings, isLoading }}>
      {children}
    </WhitelabelContext.Provider>
  );
}
